import { cross3, dot3, normalize3, sub3 } from '../vector-math.js';
import { computeGeometricAdjacency } from '../adjacency.js';
import { boundaryLoopOfFaceSet } from '../boundary.js';
import { edgeId, edgesOfTopology } from '../edges.js';
import { assertSafeIdCapacity } from '../ids.js';
import { REGION_NORMAL_EPSILON, inwardEdgeNormal } from '../regions.js';
import { assertRenderableTriangle } from '../validation.js';

// -----------------------------------------------------------------------
// Bevel (single edge, MVP1: convex hard edges with valence-3 endpoints)
// -----------------------------------------------------------------------
//
// Consumes `computeGeometricAdjacency` unchanged — this never re-derives
// regions/corners/edges, only interprets one already-computed edge record
// and rewrites the raw topology around it. See the design rationale this
// implementation follows for the full geometric argument; summarized here
// only where the code needs it.
//
// SHAPE: given geometric edge E between flanking regions P and Q, joining
// endpoint corners A and B, each with a third ("cap") region — R_A at A,
// R_B at B:
//   - P and Q are SUBSTITUTED, not rebuilt: their existing triangulation
//     (face ids, face count, internal diagonals) stays exactly as it was:
//     only the two raw vertex ids representing A and B within that region
//     are replaced by a new, slid-inward vertex.
//   - R_A and R_B are REBUILT: each loses its one raw vertex representing
//     the beveled corner and gains two (the new P-side and Q-side points),
//     then is fan-triangulated from a deterministic anchor.
//   - One new planar quad (the "strip") connects the four new vertices,
//     triangulated into exactly 2 triangles.
//
// WIDTH: the perpendicular in-plane distance each flanking region's
// boundary moves inward, computed independently in P and in Q via the
// existing `inwardEdgeNormal` — see `computeSlide` below for the per-
// endpoint, per-region construction.
//
// ELIGIBILITY is fully re-derived from `computeGeometricAdjacency`'s own
// output on every call — nothing here trusts a previously-cached result,
// matching that layer's own "call again after any mutation" contract.

// Same tightness rationale as REGION_NORMAL_EPSILON: a hard edge that
// merely LOOKS convex within floating-point noise (i.e. essentially
// coplanar-adjacent, already excluded by REGION_NORMAL_EPSILON above) must
// not slip through as "convex" — this only needs to reject the genuine
// near-zero straddle right at the coplanar boundary, not approximate
// "roughly convex enough".
const BEVEL_CONVEXITY_EPSILON = 1e-9;

// The same singularity `insetMiterPoint` already guards with an identical
// `cos < 1e-6` cutoff: as the angle between the beveled edge and its
// neighboring boundary edge approaches 0°/180° within one flanking region,
// the slide direction's component along that region's inward normal
// vanishes and no finite offset satisfies "move `width` inward" — reject
// before dividing by (near-)zero rather than let it blow up into a
// non-finite or wildly oversized position.
const BEVEL_SLIDE_DENOMINATOR_EPSILON = 1e-6;

// Same rationale and same value as INSET_MIN_RELATIVE_AMOUNT: a width small
// enough to build cleanly at this module's float64 working precision but
// too small relative to the mesh's own scale collapses to zero-area
// geometry the moment `buildGeometryFromTopology` rounds it into its
// Float32Array — see INSET_MIN_RELATIVE_AMOUNT's own comment for the full
// argument, which applies here unchanged. Checked against the LONGEST of
// the 5 edges this operation actually touches (the beveled edge itself and
// the 4 neighboring slide edges), for the same "largest plausible
// coordinate magnitude in play" reason.
const BEVEL_MIN_RELATIVE_WIDTH = 1e-6;

/** Every raw vertex id referenced by any face in `region.faceIds` — the region's own "which duplicate belongs to me" set, used to resolve which of a corner's several position-duplicate vertex ids is this region's own. */
function regionRawVertexIds(region, faceById) {
  const ids = new Set();
  for (const faceId of region.faceIds) {
    for (const vertexId of faceById.get(faceId).vertexIds) ids.add(vertexId);
  }
  return ids;
}

/** The single raw vertex id representing `corner` within `region` (the one member of `corner.vertexIds` this region's own faces actually reference), or `null` if that is not exactly one id — an unsupported corner/region relationship for bevel. */
function rawVertexOfCornerInRegion(corner, regionVertexIdSet) {
  const matches = corner.vertexIds.filter((id) => regionVertexIdSet.has(id));
  return matches.length === 1 ? matches[0] : null;
}

/** The directed pair `[from, to]`, as some face of `region` actually stores it, for the raw edge between `rawA` and `rawB` — i.e. the region's own winding direction for that specific edge. `null` if no member face carries it directly (the edge is not a literal triangle edge of this region — a structural precondition failure). */
function directedRawEdgeInRegion(region, faceById, rawA, rawB) {
  for (const faceId of region.faceIds) {
    const ids = faceById.get(faceId).vertexIds;
    for (let i = 0; i < 3; i++) {
      const a = ids[i];
      const b = ids[(i + 1) % 3];
      if ((a === rawA && b === rawB) || (a === rawB && b === rawA)) return [a, b];
    }
  }
  return null;
}

/**
 * The non-beveled-edge structure at one endpoint corner: which region caps
 * it (`capRegionId`), and which neighboring corner lies across each of the
 * two non-beveled edges incident to it (`neighborCornerIdP` on the P side,
 * `neighborCornerIdQ` on the Q side) — derived purely from the corner's own
 * `edgeIds`/`regionIds` (already validated as exactly 3 of each by the
 * caller), never by re-walking a boundary loop. A corner whose two
 * non-beveled edges don't resolve to one single shared third region, one
 * on the P side and one on the Q side, is rejected here as an unsupported
 * local shape rather than guessed at.
 */
function bevelEndpointStructure(corner, geometricEdgeId, regionPId, regionQId, edgeById, commandName) {
  const otherEdgeIds = corner.edgeIds.filter((id) => id !== geometricEdgeId);
  if (otherEdgeIds.length !== 2) {
    throw new Error(`${commandName}: corner '${corner.id}' does not have exactly 2 non-beveled incident edges`);
  }
  const otherEdges = otherEdgeIds.map((id) => edgeById.get(id));
  if (otherEdges.some((e) => !e || e.regionIds.length !== 2 || e.cornerIds.length !== 2)) {
    throw new Error(`${commandName}: corner '${corner.id}' has a non-beveled edge with unresolved incidence`);
  }
  const edgeP = otherEdges.find((e) => e.regionIds.includes(regionPId));
  const edgeQ = otherEdges.find((e) => e.regionIds.includes(regionQId) && e !== edgeP);
  if (!edgeP || !edgeQ) {
    throw new Error(`${commandName}: corner '${corner.id}' does not have one non-beveled edge per flanking region — unsupported corner structure for bevel`);
  }
  const capRegionIdViaP = edgeP.regionIds.find((id) => id !== regionPId);
  const capRegionIdViaQ = edgeQ.regionIds.find((id) => id !== regionQId);
  if (!capRegionIdViaP || !capRegionIdViaQ || capRegionIdViaP !== capRegionIdViaQ) {
    throw new Error(`${commandName}: corner '${corner.id}' has an inconsistent third (cap) region between its two non-beveled edges`);
  }
  const neighborCornerIdP = edgeP.cornerIds.find((id) => id !== corner.id);
  const neighborCornerIdQ = edgeQ.cornerIds.find((id) => id !== corner.id);
  if (!neighborCornerIdP || !neighborCornerIdQ) {
    throw new Error(`${commandName}: corner '${corner.id}' has a malformed non-beveled edge`);
  }
  return { capRegionId: capRegionIdViaP, neighborCornerIdP, neighborCornerIdQ };
}

/**
 * The pure computation behind `bevelEdge`: resolves `geometricEdgeId`
 * against a freshly computed `computeGeometricAdjacency(topology)`,
 * validates every MVP1 eligibility rule, computes the 4 new vertex
 * positions and every new/modified face, and validates the complete
 * constructed result — all without writing anything to `topology`. Throws
 * on the first violated rule; `commitBevelEdge` is the only function that
 * may mutate `topology`, and only after this returns successfully.
 */
export function computeBevelEdge(topology, geometricEdgeId, width, commandName = 'bevelEdge') {
  if (typeof geometricEdgeId !== 'string' || geometricEdgeId.length === 0) {
    throw new Error(`${commandName} requires a non-empty "edgeId"`);
  }
  if (!Number.isFinite(width) || width <= 0) {
    throw new Error(`${commandName} requires a positive finite "width"`);
  }

  const adjacency = computeGeometricAdjacency(topology);
  const edgeById = new Map(adjacency.edges.map((e) => [e.id, e]));
  const regionById = new Map(adjacency.regions.map((r) => [r.id, r]));
  const cornerById = new Map(adjacency.corners.map((c) => [c.id, c]));

  const edge = edgeById.get(geometricEdgeId);
  if (!edge) {
    // A specific, useful error for the most likely caller mistake — passing
    // a raw topology edge id (from edgesOfTopology / current Edge-mode
    // picking) or a known internal diagonal instead of a derived geometric
    // edge id from getMeshAdjacency — rather than only "not found", which
    // would be equally true for a genuinely stale id after some other edit.
    // Diagonal ids are themselves a subset of `edgesOfTopology`'s output (a
    // diagonal is a raw edge with 2 face incidences too), so that more
    // specific check must run FIRST — checking the raw-edge set first would
    // make the diagonal branch below unreachable.
    if (adjacency.diagonalTopologyEdgeIds.includes(geometricEdgeId)) {
      throw new Error(`${commandName}: '${geometricEdgeId}' is an internal triangulation diagonal, not a visible geometric edge, and cannot be beveled`);
    }
    const rawEdgeIds = new Set(edgesOfTopology(topology).map((e) => e.id));
    if (rawEdgeIds.has(geometricEdgeId)) {
      throw new Error(
        `${commandName}: '${geometricEdgeId}' is a raw topology edge id, not a geometric edge id — call getMeshAdjacency and pass one of its edges[].id values`,
      );
    }
    throw new Error(`${commandName}: unknown or stale geometric edge id '${geometricEdgeId}' — recompute getMeshAdjacency against the current topology`);
  }

  if (edge.cornerIds.length !== 2 || edge.cornerIds[0] === edge.cornerIds[1]) {
    throw new Error(`${commandName}: edge '${geometricEdgeId}' does not have exactly 2 distinct endpoint corners`);
  }
  if (edge.regionIds.length !== 2) {
    throw new Error(
      `${commandName}: edge '${geometricEdgeId}' has ${edge.regionIds.length} incident visible region(s), not exactly 2 (an open boundary or ambiguous/coalesced incidence is not supported)`,
    );
  }
  if (edge.topologyEdgeIds.length === 0 || edge.topologyEdgeIds.some((id) => id == null)) {
    throw new Error(`${commandName}: edge '${geometricEdgeId}' is missing its raw topology edge ids`);
  }

  const [cornerAId, cornerBId] = edge.cornerIds;
  const cornerA = cornerById.get(cornerAId);
  const cornerB = cornerById.get(cornerBId);
  const [regionPId, regionQId] = edge.regionIds;
  const regionP = regionById.get(regionPId);
  const regionQ = regionById.get(regionQId);

  for (const region of [regionP, regionQ]) {
    if (
      !Array.isArray(region.normal) || region.normal.length !== 3
      || !region.normal.every(Number.isFinite) || Math.hypot(...region.normal) < 1e-9
    ) {
      throw new Error(`${commandName}: region '${region.id}' has no valid normal`);
    }
  }
  if (dot3(regionP.normal, regionQ.normal) > 1 - REGION_NORMAL_EPSILON) {
    throw new Error(`${commandName}: edge '${geometricEdgeId}' has coplanar incident regions — nothing to chamfer`);
  }
  const edgeLength = Math.hypot(...sub3(cornerB.position, cornerA.position));
  if (!(edgeLength > 1e-9)) {
    throw new Error(`${commandName}: edge '${geometricEdgeId}' has zero length`);
  }
  for (const corner of [cornerA, cornerB]) {
    if (corner.regionIds.length !== 3 || corner.edgeIds.length !== 3) {
      throw new Error(
        `${commandName}: corner '${corner.id}' has ${corner.regionIds.length} incident region(s) and ${corner.edgeIds.length} incident edge(s) — bevel MVP1 requires exactly 3 of each at both endpoints`,
      );
    }
  }

  const faceById = new Map(topology.faces.map((f) => [f.id, f]));
  const positionById = new Map(topology.vertices.map((v) => [v.id, v.position]));
  const cornerIdOfVertex = new Map();
  for (const corner of adjacency.corners) {
    for (const vertexId of corner.vertexIds) cornerIdOfVertex.set(vertexId, corner.id);
  }

  const pVertexIds = regionRawVertexIds(regionP, faceById);
  const qVertexIds = regionRawVertexIds(regionQ, faceById);

  const rA_P = rawVertexOfCornerInRegion(cornerA, pVertexIds);
  const rB_P = rawVertexOfCornerInRegion(cornerB, pVertexIds);
  const rA_Q = rawVertexOfCornerInRegion(cornerA, qVertexIds);
  const rB_Q = rawVertexOfCornerInRegion(cornerB, qVertexIds);
  for (const [label, raw] of [['A in P', rA_P], ['B in P', rB_P], ['A in Q', rA_Q], ['B in Q', rB_Q]]) {
    if (raw == null) {
      throw new Error(`${commandName}: endpoint ${label} does not occur exactly once in that flanking region's boundary — unsupported corner structure`);
    }
  }
  const topologyEdgeIdSet = new Set(edge.topologyEdgeIds);
  if (!topologyEdgeIdSet.has(edgeId(rA_P, rB_P)) || !topologyEdgeIdSet.has(edgeId(rA_Q, rB_Q))) {
    throw new Error(`${commandName}: edge '${geometricEdgeId}' raw endpoint reconstruction does not match its own topologyEdgeIds`);
  }

  const directedP = directedRawEdgeInRegion(regionP, faceById, rA_P, rB_P);
  const directedQ = directedRawEdgeInRegion(regionQ, faceById, rA_Q, rB_Q);
  if (!directedP || !directedQ) {
    throw new Error(`${commandName}: could not locate the beveled edge within one of its flanking regions' own faces`);
  }
  const uP = inwardEdgeNormal(regionP.normal, positionById.get(directedP[0]), positionById.get(directedP[1]));
  const uQ = inwardEdgeNormal(regionQ.normal, positionById.get(directedQ[0]), positionById.get(directedQ[1]));
  if (!uP || !uQ) {
    throw new Error(`${commandName}: could not compute an inward direction for edge '${geometricEdgeId}' (a zero-length edge)`);
  }

  // Convexity (rule 11): dot(u_P, n_Q) < 0 — P's inward direction points
  // against Q's own outward normal exactly when the dihedral fold is
  // convex. Symmetric in P/Q by construction (checking the other pairing
  // is redundant, not a second independent test).
  if (dot3(uP, regionQ.normal) >= -BEVEL_CONVEXITY_EPSILON) {
    throw new Error(`${commandName}: edge '${geometricEdgeId}' is not convex — bevel MVP1 supports convex hard edges only`);
  }

  const structA = bevelEndpointStructure(cornerA, geometricEdgeId, regionPId, regionQId, edgeById, commandName);
  const structB = bevelEndpointStructure(cornerB, geometricEdgeId, regionPId, regionQId, edgeById, commandName);
  if (structA.capRegionId === structB.capRegionId) {
    throw new Error(`${commandName}: both endpoints of edge '${geometricEdgeId}' share the same cap region — unsupported by bevel MVP1`);
  }
  const capA = regionById.get(structA.capRegionId);
  const capB = regionById.get(structB.capRegionId);
  if (!capA || !capB) {
    throw new Error(`${commandName}: could not resolve a cap region for edge '${geometricEdgeId}'`);
  }

  // Gather the 4 neighboring-edge geometries (direction + length) before
  // computing any offset, so the scale-relative minimum width can be
  // checked once, up front, against every edge this operation touches —
  // same ordering `insetFaceSet` uses for its own lower-bound check.
  function neighborGeometry(rawInRegion, neighborCornerId, regionVertexIdSet, label) {
    const neighborCorner = cornerById.get(neighborCornerId);
    const neighborRaw = rawVertexOfCornerInRegion(neighborCorner, regionVertexIdSet);
    if (neighborRaw == null) {
      throw new Error(`${commandName}: ${label}'s neighboring corner does not occur exactly once in that flanking region — unsupported corner structure`);
    }
    const fromPos = positionById.get(rawInRegion);
    const toPos = positionById.get(neighborRaw);
    const length = Math.hypot(...sub3(toPos, fromPos));
    const direction = normalize3(sub3(toPos, fromPos));
    if (!direction || !(length > 1e-9)) {
      throw new Error(`${commandName}: ${label} has a degenerate neighboring edge`);
    }
    return { neighborRaw, fromPos, direction, length };
  }

  const geomAP = neighborGeometry(rA_P, structA.neighborCornerIdP, pVertexIds, 'endpoint A in region P');
  const geomAQ = neighborGeometry(rA_Q, structA.neighborCornerIdQ, qVertexIds, 'endpoint A in region Q');
  const geomBP = neighborGeometry(rB_P, structB.neighborCornerIdP, pVertexIds, 'endpoint B in region P');
  const geomBQ = neighborGeometry(rB_Q, structB.neighborCornerIdQ, qVertexIds, 'endpoint B in region Q');

  const maxRelevantEdgeLength = Math.max(edgeLength, geomAP.length, geomAQ.length, geomBP.length, geomBQ.length);
  if (width < BEVEL_MIN_RELATIVE_WIDTH * maxRelevantEdgeLength) {
    throw new Error(`${commandName}: width ${width} is too small for this edge (the result would collapse to zero-area geometry at render precision)`);
  }

  function slidePoint(geom, u, label) {
    const denom = dot3(geom.direction, u);
    if (!(denom > BEVEL_SLIDE_DENOMINATOR_EPSILON)) {
      throw new Error(`${commandName}: ${label} is too sharp/straight for a bevel offset to be computed`);
    }
    const t = width / denom;
    if (!Number.isFinite(t) || !(t > 0)) {
      throw new Error(`${commandName}: computed an invalid slide distance at ${label}`);
    }
    if (t >= geom.length) {
      throw new Error(`${commandName}: width ${width} is too large at ${label} (the offset would reach or pass the neighboring corner)`);
    }
    const position = [geom.fromPos[0] + geom.direction[0] * t, geom.fromPos[1] + geom.direction[1] * t, geom.fromPos[2] + geom.direction[2] * t];
    if (!position.every(Number.isFinite)) {
      throw new Error(`${commandName}: ${label} produced a non-finite position`);
    }
    return position;
  }

  const posAP = slidePoint(geomAP, uP, 'endpoint A in region P');
  const posAQ = slidePoint(geomAQ, uQ, 'endpoint A in region Q');
  const posBP = slidePoint(geomBP, uP, 'endpoint B in region P');
  const posBQ = slidePoint(geomBQ, uQ, 'endpoint B in region Q');

  // Deterministic id mint order (rule from the design): A/P, A/Q, B/P, B/Q.
  let nextVertexId = topology.nextVertexId;
  const idAP = `v${nextVertexId++}`;
  const idAQ = `v${nextVertexId++}`;
  const idBP = `v${nextVertexId++}`;
  const idBQ = `v${nextVertexId++}`;
  const newVertices = [
    { id: idAP, position: posAP },
    { id: idAQ, position: posAQ },
    { id: idBP, position: posBP },
    { id: idBQ, position: posBQ },
  ];
  const newPositionById = new Map(newVertices.map((v) => [v.id, v.position]));
  const positionOf = (id) => newPositionById.get(id) ?? positionById.get(id);

  // Cap rebuild: splice the endpoint's own single raw vertex out of that
  // cap region's boundary loop, replacing it with the two new points in
  // whichever order matches the loop's own P-side/Q-side neighbors — never
  // assumed, always confirmed against the corner ids on either side.
  function rebuildCap(cap, corner, struct, newIdP, newIdQ, label) {
    const capVertexIds = regionRawVertexIds(cap, faceById);
    const rawInCap = rawVertexOfCornerInRegion(corner, capVertexIds);
    if (rawInCap == null) {
      throw new Error(`${commandName}: ${label} does not occur exactly once in its cap region '${cap.id}'`);
    }
    const capFaces = cap.faceIds.map((id) => faceById.get(id));
    const loop = boundaryLoopOfFaceSet(capFaces, commandName);
    const occurrences = loop.filter((id) => id === rawInCap).length;
    if (occurrences !== 1) {
      throw new Error(`${commandName}: cap region '${cap.id}' boundary does not visit ${label} exactly once — unsupported`);
    }
    const j = loop.indexOf(rawInCap);
    const n = loop.length;
    const prevCorner = cornerIdOfVertex.get(loop[(j - 1 + n) % n]);
    const nextCorner = cornerIdOfVertex.get(loop[(j + 1) % n]);
    let insertion;
    if (prevCorner === struct.neighborCornerIdP && nextCorner === struct.neighborCornerIdQ) {
      insertion = [newIdP, newIdQ];
    } else if (prevCorner === struct.neighborCornerIdQ && nextCorner === struct.neighborCornerIdP) {
      insertion = [newIdQ, newIdP];
    } else {
      throw new Error(`${commandName}: cap region '${cap.id}' does not connect consistently to both flanking regions at ${label} — unsupported cap shape`);
    }
    const newLoop = [...loop.slice(0, j), ...insertion, ...loop.slice(j + 1)];
    // Rotate so the fan's deterministic anchor (the first newly inserted
    // point) sits at index 0 — the anchor choice, not the rotation itself,
    // is what needs to be deterministic; rotating to it is just how a cyclic
    // loop expresses "start here" without disturbing winding.
    const anchorIndex = newLoop.indexOf(insertion[0]);
    const rotated = [...newLoop.slice(anchorIndex), ...newLoop.slice(0, anchorIndex)];
    if (rotated.length < 3) {
      throw new Error(`${commandName}: cap region '${cap.id}' would have fewer than 3 boundary vertices after bevel — unsupported`);
    }
    return { rawInCap, rotatedLoop: rotated };
  }

  const capRebuildA = rebuildCap(capA, cornerA, structA, idAP, idAQ, `endpoint A (corner '${cornerA.id}')`);
  const capRebuildB = rebuildCap(capB, cornerB, structB, idBP, idBQ, `endpoint B (corner '${cornerB.id}')`);

  // Exact mint counts, known before minting any face id — same "exact, not
  // worst-case" discipline `assertSafeIdCapacity`'s own doc requires.
  const capAFaceCount = capRebuildA.rotatedLoop.length - 2;
  const capBFaceCount = capRebuildB.rotatedLoop.length - 2;
  const faceMintCount = capAFaceCount + capBFaceCount + 2;
  assertSafeIdCapacity(topology.nextVertexId, 4, topology.nextFaceId, faceMintCount, commandName);

  let nextFaceId = topology.nextFaceId;
  const newFaces = [];

  function fanTriangulate(rotatedLoop, expectedNormal, faceLabelPrefix) {
    const anchor = rotatedLoop[0];
    for (let i = 1; i < rotatedLoop.length - 1; i++) {
      const vertexIds = [anchor, rotatedLoop[i], rotatedLoop[i + 1]];
      const positions = vertexIds.map(positionOf);
      assertRenderableTriangle(commandName, `${faceLabelPrefix} triangle`, positions);
      const [a, b, c] = positions;
      const cross = cross3(sub3(b, a), sub3(c, a));
      const crossLength = Math.hypot(...cross);
      const normal = cross.map((n) => n / crossLength);
      if (dot3(normal, expectedNormal) < 1 - REGION_NORMAL_EPSILON) {
        throw new Error(`${commandName}: ${faceLabelPrefix} would fold or invert relative to its region's normal — unsupported cap shape for this width`);
      }
      newFaces.push({ id: `f${nextFaceId++}`, vertexIds });
    }
  }

  fanTriangulate(capRebuildA.rotatedLoop, capA.normal, `cap region '${capA.id}' (endpoint A)`);
  fanTriangulate(capRebuildB.rotatedLoop, capB.normal, `cap region '${capB.id}' (endpoint B)`);

  // Bevel strip: the quad (A_P, B_P, B_Q, A_Q), split into 2 triangles.
  // Winding is chosen, not assumed — evaluated against the geometrically
  // deterministic reference direction normalize(n_P + n_Q), which for a
  // convex edge always points to the same side the true outward strip
  // normal does, regardless of which arbitrary raw-id ordering `directedP`/
  // `directedQ` happened to come back as.
  const roughOutward = normalize3([regionP.normal[0] + regionQ.normal[0], regionP.normal[1] + regionQ.normal[1], regionP.normal[2] + regionQ.normal[2]]);
  if (!roughOutward) {
    throw new Error(`${commandName}: edge '${geometricEdgeId}' has opposing region normals and no well-defined outward strip direction`);
  }
  function quadNormal(order) {
    const [a, b, , d] = order.map(positionOf);
    const cross = cross3(sub3(b, a), sub3(d, a));
    const length = Math.hypot(...cross);
    return length < 1e-12 ? null : cross.map((n) => n / length);
  }
  const forwardOrder = [idAP, idBP, idBQ, idAQ];
  const forwardNormal = quadNormal(forwardOrder);
  const stripOrder = forwardNormal && dot3(forwardNormal, roughOutward) > 0 ? forwardOrder : [idAP, idAQ, idBQ, idBP];

  function stripTriangle(ids, label) {
    const positions = ids.map(positionOf);
    assertRenderableTriangle(commandName, label, positions);
    const [a, b, c] = positions;
    const cross = cross3(sub3(b, a), sub3(c, a));
    const crossLength = Math.hypot(...cross);
    const normal = cross.map((n) => n / crossLength);
    if (dot3(normal, roughOutward) <= 0) {
      throw new Error(`${commandName}: ${label} would face inward — unsupported width/shape for this edge`);
    }
    const face = { id: `f${nextFaceId++}`, vertexIds: ids };
    newFaces.push(face);
    return face;
  }
  const strip1 = stripTriangle([stripOrder[0], stripOrder[1], stripOrder[2]], `bevel strip triangle (edge '${geometricEdgeId}')`);
  const strip2 = stripTriangle([stripOrder[0], stripOrder[2], stripOrder[3]], `bevel strip triangle (edge '${geometricEdgeId}')`);

  // Substitution for P and Q: existing face ids preserved, only the two
  // raw vertex ids representing A and B within that region are replaced.
  function substituteRegion(region, rawA, newA, rawB, newB) {
    const substitutions = [];
    for (const faceId of region.faceIds) {
      const face = faceById.get(faceId);
      let changed = false;
      const vertexIds = face.vertexIds.map((id) => {
        if (id === rawA) { changed = true; return newA; }
        if (id === rawB) { changed = true; return newB; }
        return id;
      });
      if (changed) substitutions.push({ faceId, vertexIds });
    }
    return substitutions;
  }
  const substitutionsP = substituteRegion(regionP, rA_P, idAP, rB_P, idBP);
  const substitutionsQ = substituteRegion(regionQ, rA_Q, idAQ, rB_Q, idBQ);
  const substitutions = [...substitutionsP, ...substitutionsQ];
  if (substitutions.length === 0) {
    throw new Error(`${commandName}: edge '${geometricEdgeId}' produced no substituted faces in its flanking regions — unsupported`);
  }

  const removedFaceIds = [...capA.faceIds, ...capB.faceIds];
  const removedVertexIds = [rA_P, rB_P, rA_Q, rB_Q, capRebuildA.rawInCap, capRebuildB.rawInCap];

  // Final dangling-reference check over the complete post-bevel face set —
  // cheap, and the strongest single guarantee that nothing above silently
  // left a face referencing a vertex this operation is about to remove.
  const removedVertexIdSet = new Set(removedVertexIds);
  const survivingVertexIds = new Set(topology.vertices.map((v) => v.id).filter((id) => !removedVertexIdSet.has(id)));
  for (const v of newVertices) survivingVertexIds.add(v.id);
  const substitutedFaceIds = new Set(substitutions.map((s) => s.faceId));
  const finalFaces = [
    ...topology.faces.filter((f) => !removedFaceIds.includes(f.id) && !substitutedFaceIds.has(f.id)),
    ...substitutions.map((s) => ({ id: s.faceId, vertexIds: s.vertexIds })),
    ...newFaces,
  ];
  const finalRawEdgeIds = new Set(edgesOfTopology({ faces: finalFaces }).map((rawEdge) => rawEdge.id));
  for (const explicitEdgeId of topology.explicitEdgeIds ?? []) {
    if (!finalRawEdgeIds.has(explicitEdgeId)) {
      throw new Error(`${commandName}: bevel would destroy explicit edge '${explicitEdgeId}'`);
    }
  }
  for (const face of finalFaces) {
    for (const vertexId of face.vertexIds) {
      if (!survivingVertexIds.has(vertexId)) {
        throw new Error(`${commandName}: internal inconsistency — face '${face.id}' would reference removed vertex '${vertexId}'`);
      }
    }
  }
  const faceIdCounts = new Map();
  for (const face of finalFaces) faceIdCounts.set(face.id, (faceIdCounts.get(face.id) ?? 0) + 1);
  for (const [id, count] of faceIdCounts) {
    if (count > 1) throw new Error(`${commandName}: internal inconsistency — duplicate generated face id '${id}'`);
  }

  return {
    removedFaceIds,
    removedVertexIds,
    substitutions,
    newVertices,
    newFaces,
    bevelFaceIds: [strip1.id, strip2.id],
    affectedFaceIds: substitutions.map((s) => s.faceId).sort(),
    nextVertexId,
    nextFaceId,
  };
}

/** Write a precomputed `computeBevelEdge` result into `topology` — the single mutation point, mirroring `commitSubdivision`. */
function commitBevelEdge(topology, computed) {
  const removedFaceIdSet = new Set(computed.removedFaceIds);
  const removedVertexIdSet = new Set(computed.removedVertexIds);
  const substitutionByFaceId = new Map(computed.substitutions.map((s) => [s.faceId, s.vertexIds]));

  topology.vertices = topology.vertices.filter((v) => !removedVertexIdSet.has(v.id));
  topology.vertices.push(...computed.newVertices);

  topology.faces = topology.faces
    .filter((f) => !removedFaceIdSet.has(f.id))
    .map((f) => (substitutionByFaceId.has(f.id) ? { id: f.id, vertexIds: substitutionByFaceId.get(f.id) } : f));
  topology.faces.push(...computed.newFaces);

  topology.nextVertexId = computed.nextVertexId;
  topology.nextFaceId = computed.nextFaceId;
}

/**
 * Bevel one geometric edge — MVP1: a convex hard edge whose two endpoint
 * geometric corners each have exactly 3 incident visible regions and 3
 * incident geometric edges (see `computeBevelEdge`'s own doc for the full
 * algorithm). `geometricEdgeId` must be an `edges[].id` from a *current*
 * `computeGeometricAdjacency(topology)` call — never a raw topology edge id
 * (from `edgesOfTopology`) and never an `diagonalTopologyEdgeIds` entry;
 * both are rejected with a specific error rather than only "not found".
 *
 * All-or-nothing, like every other topology-mutating operation in this
 * module: `computeBevelEdge` performs every lookup, validation, and
 * geometry computation — including validating the complete constructed
 * result — before `commitBevelEdge` writes anything.
 */
export function bevelEdge(topology, geometricEdgeId, width) {
  const computed = computeBevelEdge(topology, geometricEdgeId, width, 'bevelEdge');
  commitBevelEdge(topology, computed);
  return {
    bevelFaceIds: computed.bevelFaceIds,
    newVertexIds: computed.newVertices.map((v) => v.id),
    newFaceIds: computed.newFaces.map((f) => f.id),
    removedFaceIds: computed.removedFaceIds,
    affectedFaceIds: computed.affectedFaceIds,
  };
}
